import type { CallLanguage } from './language'
import {
  CUSTOM_CHARACTER_DEFAULT_VOICE,
  CUSTOM_CHARACTER_VOICE_PREVIEW_MANIFEST,
  CUSTOM_CHARACTER_VOICES,
  type CustomCharacterVoice,
} from './custom-character-voice-previews'

export {
  CUSTOM_CHARACTER_DEFAULT_VOICE,
  CUSTOM_CHARACTER_VOICE_PREVIEW_LOCALES,
  CUSTOM_CHARACTER_VOICE_PREVIEW_MANIFEST,
  CUSTOM_CHARACTER_VOICES,
  customCharacterVoicePreviewPath,
} from './custom-character-voice-previews'
export type {
  CustomCharacterVoice,
  CustomCharacterVoicePreviewLocale,
} from './custom-character-voice-previews'

/**
 * Id del personaje propio. Es una CONSTANTE, no un uuid: hay uno por cuenta,
 * así que el servidor resuelve cuál desde el `parentId` autenticado. Sin id
 * enumerable en URLs ni bodies no hay IDOR que defender.
 */
export const CUSTOM_CHARACTER_ID = 'custom' as const
export type CustomCharacterId = typeof CUSTOM_CHARACTER_ID

export function isCustomCharacterId(value: unknown): value is CustomCharacterId {
  return value === CUSTOM_CHARACTER_ID
}

export function isCustomCharacterVoice(value: unknown): value is CustomCharacterVoice {
  return (CUSTOM_CHARACTER_VOICES as readonly unknown[]).includes(value)
}

/** Topes del spec, en un solo sitio para que no se decidan en el código. */
export const CUSTOM_CHARACTER_LIMITS = {
  nameMaxLength: 40,
  briefMaxLength: 600,
  personaSectionMaxLength: 1200,
  openingTextMaxLength: 300,
  agentPromptMaxLength: 200,
  imageMaxBytes: 4 * 1024 * 1024,
  attemptsPerDay: 5,
} as const

export interface CustomCharacter {
  id: string
  parentId: string
  /** Nombre final, el que dice el personaje (salido del generador). */
  displayName: string
  /** Lo que tecleó el padre; auditoría y "rehacer", nunca el prompt. */
  displayNameInput: string
  sourceBrief: string
  language: CallLanguage
  voice: CustomCharacterVoice
  imagePath: string
  imageUrl: string
  personaSection: string
  openingText: string
  trialOpeningText: string | null
  agentPrompt: string | null
  agentIdlePrompt: string | null
  disabledAt: Date | null
  createdAt: Date
  updatedAt: Date
}

/**
 * Kill-switch por personaje: al ser nosotros la única moderación de la cadena
 * (LemonSlice no documenta ninguna), hace falta poder tumbar uno concreto sin
 * apagar la feature (`CUSTOM_CHARACTER_ENABLED`) para todos.
 */
export function isCustomCharacterEnabled(character: CustomCharacter): boolean {
  return character.disabledAt === null
}

/**
 * La persona se generó en UN idioma, así que servirla a una llamada en otro
 * daría un personaje que habla en un idioma con instrucciones en otro.
 */
export function matchesCallLanguage(
  character: CustomCharacter,
  callLanguage: CallLanguage
): boolean {
  return character.language === callLanguage
}

export type CustomCharacterAttemptOutcome = 'accepted' | 'rejected'

/**
 * Veredicto del gate persistido junto al personaje: versión de política,
 * intento de inyección y cuándo se aceptó. Único llamador
 * (`CustomCharacterService.createAfterGateAccepted`), siempre con esta forma
 * concreta — declararla evita que `SaveCustomCharacterInput.moderation` acepte
 * cualquier cosa en un contrato de dominio.
 */
export interface CustomCharacterModerationRecord {
  policyVersion: string
  injectionSuspected: boolean
  acceptedAt: string
}
